import { Injectable } from "@angular/core";
import { CanActivate } from "@angular/router";
import { FuncionesServiceProvider } from "src/app/service/index.providers";
import { LoginMultiModuloService } from "src/app/service/login-multi-modulo/login-multi-modulo.service";
import { isPerformanceMockEnabled } from "src/app/service/modulo-desempeno/mock/mock-config";
import { PerformanceThemeRefreshService } from "src/app/service/modulo-desempeno/performance-theme-refresh.service";

/**
 * Acceso al módulo de Desempeño. Va DESPUÉS de `CheckPagesGuard` en la ruta
 * `performance`, así que puede asumir que ya hay sesión: lo que valida son las
 * tres condiciones propias del módulo, que hasta ahora solo garantizaban las
 * puertas de entrada del app (`toPerformanceModule` de los side-menus y
 * `resumeInPerformance` del login) y que un deep-link o un refresh se saltaban.
 */
@Injectable({ providedIn: "root" })
export class PerformanceAccessGuard implements CanActivate {
	constructor(
		private loginMultiModuloService: LoginMultiModuloService,
		private fp: FuncionesServiceProvider,
		private themeRefresh: PerformanceThemeRefreshService,
	) {}

	async canActivate(): Promise<boolean> {
		// Con el mock activo no hay backend que valide nada: se omite el flag para
		// no bloquear el desarrollo local con usuarios simulados sin `has_performance`
		// (mismo criterio con el que `ensurePerformanceToken` corto-circuita).
		if (!isPerformanceMockEnabled() && !this.loginMultiModuloService.hasPerformanceAccess) {
			this.loginMultiModuloService.navigateToOriginPlatform();
			return false;
		}

		// Barato en el caso normal (sale por el token ya cacheado); cubre el
		// deep-link/refresh en el que nadie hizo el handshake de la plataforma.
		const tokenReady = await this.loginMultiModuloService.ensurePerformanceToken();
		if (!tokenReady) {
			this.loginMultiModuloService.navigateToOriginPlatform();
			return false;
		}

		// Entrando por URL nadie marcó la plataforma, y `cargarFunciones()` arma el
		// menú de capacitación/inducción sobre el módulo de Desempeño. Solo se
		// reconstruye cuando el flag no estaba, para no rehacer el menú en cada
		// activación.
		if (localStorage.getItem("isPerformanceAccessSelected") !== "true") {
			localStorage.setItem("isPerformanceAccessSelected", "true");
			this.fp.cargarFunciones();
		}

		// Los colores de Personalización → Aplicación se piden en cada entrada al módulo,
		// sin esperar: la navegación no depende de esta llamada (`refresh()` no lanza).
		if (!isPerformanceMockEnabled()) void this.themeRefresh.refresh();

		return true;
	}
}
